import * as React from 'react';
import { Search, Bell, Sun, Moon, Sparkles } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../button';

/**
 * Header component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Height: 52px
 * - Background: bg-secondary
 * - Border: 1px bottom grey-200
 * - Search: 480px max width, 34px height
 * - Icon buttons: 34px square, 6px radius
 */

export const Header = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <header
        ref={ref}
        className={cn(
          'h-[52px] bg-white border-b border-grey-200 flex items-center px-4 gap-3 sticky top-0 z-30',
          'dark:bg-grey-900 dark:border-grey-800',
          className
        )}
        {...props}
      >
        {children}
      </header>
    );
  }
);
Header.displayName = 'Header';

export const HeaderSearch = React.forwardRef(
  ({ className, placeholder = 'Search...', shortcut = '⌘K', onClick, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex-1 max-w-[480px] relative', className)}
        onClick={onClick}
        {...props}
      >
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
        <input
          type="text"
          placeholder={placeholder}
          readOnly
          className={cn(
            'w-full h-[34px] rounded-md bg-grey-100 border border-grey-200 pl-9 pr-14 text-sm',
            'text-grey-900 placeholder:text-grey-400 cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500',
            'dark:bg-grey-800 dark:border-grey-700 dark:text-grey-50 dark:placeholder:text-grey-500'
          )}
        />
        {shortcut && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-mono text-grey-400 bg-grey-200 dark:bg-grey-700 px-1.5 py-0.5 rounded">
            {shortcut}
          </span>
        )}
      </div>
    );
  }
);
HeaderSearch.displayName = 'HeaderSearch';

export const HeaderActions = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center gap-2', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
HeaderActions.displayName = 'HeaderActions';

export const HeaderIconButton = React.forwardRef(
  ({ className, icon: Icon, badge, variant = 'ghost', ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant={variant}
        size="icon"
        className={cn(
          'w-[34px] h-[34px] relative',
          variant === 'ghost' && 'text-grey-500 hover:text-grey-900 dark:text-grey-400 dark:hover:text-grey-50',
          className
        )}
        {...props}
      >
        {Icon && <Icon className="w-5 h-5" />}
        {badge && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white dark:border-grey-900" />
        )}
      </Button>
    );
  }
);
HeaderIconButton.displayName = 'HeaderIconButton';

export const HeaderAIButton = React.forwardRef(
  ({ className, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        variant="default"
        size="icon"
        className={cn(
          'w-[34px] h-[34px] bg-gradient-to-br from-teal-500 to-cyan-500 border-0',
          'hover:from-teal-600 hover:to-cyan-600',
          className
        )}
        {...props}
      >
        <Sparkles className="w-[18px] h-[18px]" />
      </Button>
    );
  }
);
HeaderAIButton.displayName = 'HeaderAIButton';

export const HeaderThemeToggle = React.forwardRef(
  ({ className, darkMode, onToggle, ...props }, ref) => {
    return (
      <HeaderIconButton
        ref={ref}
        icon={darkMode ? Sun : Moon}
        onClick={onToggle}
        aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        className={className}
        {...props}
      />
    );
  }
);
HeaderThemeToggle.displayName = 'HeaderThemeToggle';

export const HeaderNotifications = React.forwardRef(
  ({ className, hasUnread = false, ...props }, ref) => {
    return (
      <HeaderIconButton
        ref={ref}
        icon={Bell}
        badge={hasUnread}
        aria-label="Notifications"
        className={className}
        {...props}
      />
    );
  }
);
HeaderNotifications.displayName = 'HeaderNotifications';
